import { Elements } from '@stripe/react-stripe-js';
import {
  Appearance,
  CssFontSource,
  CustomFontSource,
  loadStripe,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import { FC, PropsWithChildren, useMemo, useRef } from 'react';

import { useCheckoutCtx } from '../../../providers/CheckoutProvider';

const ELEMENTS_APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    colorText: 'rgb(29, 22, 17)',
    colorDanger: 'rgb(255, 82, 7)',
    colorDangerText: 'rgb(255, 82, 7)',
    colorSuccess: 'rgb(5, 123, 79)',
    colorSuccessText: 'rgb(5, 123, 79)',
    colorPrimaryText: 'rgb(43, 32, 17)',
    colorTextPlaceholder: 'rgb(139, 121, 101)',
    fontSizeSm: '1rem',
    fontFamily: '"Rima Avant Arte", "Arial", "sans-serif"',
  },
  rules: {
    '.Label': {
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    '.Input': {
      fontSize: '1rem',
      fontWeight: '400',
      lineHeight: '1.25rem',
      boxShadow: 'none',
    },
  },
};

const ELEMENTS_FONTS: (CssFontSource | CustomFontSource)[] = [
  {
    family: 'Rima Avant Arte',
    src: 'url(https://avantarte.com/fonts/RimaAvant-Arte-Regular.woff2)',
    weight: 'normal',
    style: 'normal',
    display: 'swap',
  },
];

export const CheckoutElementsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { currencyCode } = useCheckoutCtx();

  const stripePromise = useRef<ReturnType<typeof loadStripe> | null>(null);
  if (stripePromise.current === null) {
    stripePromise.current = loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY!);
  }

  const options = useMemo<StripeElementsOptions>(() => {
    return {
      mode: 'setup',
      currency: currencyCode.toLowerCase(),
      appearance: ELEMENTS_APPEARANCE,
      fonts: ELEMENTS_FONTS,
    };
  }, [currencyCode]);

  return (
    <Elements options={options} stripe={stripePromise.current}>
      {children}
    </Elements>
  );
};
